/**
 * Settings Page
 *
 * User profile settings including password change and account deletion.
 */

import { A, useNavigate } from '@solidjs/router';
import { type Component, createSignal, Show } from 'solid-js';
import { getAccessToken, signOut, user } from '../lib/supabase';

const API_BASE = import.meta.env.VITE_API_URL || 'https://api.aigentswork.com';

const Settings: Component = () => {
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = createSignal('');
  const [newPassword, setNewPassword] = createSignal('');
  const [confirmPassword, setConfirmPassword] = createSignal('');
  const [passwordError, setPasswordError] = createSignal<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = createSignal(false);
  const [passwordLoading, setPasswordLoading] = createSignal(false);

  const [showDeleteConfirm, setShowDeleteConfirm] = createSignal(false);
  const [deleteConfirmText, setDeleteConfirmText] = createSignal('');
  const [deleteLoading, setDeleteLoading] = createSignal(false);
  const [deleteError, setDeleteError] = createSignal<string | null>(null);

  const currentUser = user();

  const handlePasswordChange = async (e: Event) => {
    e.preventDefault();
    setPasswordError(null);
    setPasswordSuccess(false);

    if (newPassword() !== confirmPassword()) {
      setPasswordError('Passwords do not match');
      return;
    }

    if (newPassword().length < 8) {
      setPasswordError('Password must be at least 8 characters');
      return;
    }

    setPasswordLoading(true);

    try {
      const token = await getAccessToken();
      if (!token) throw new Error('Not authenticated');

      const response = await fetch(`${API_BASE}/api/me/password`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          currentPassword: currentPassword(),
          newPassword: newPassword(),
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to change password');
      }

      setPasswordSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setPasswordError(err instanceof Error ? err.message : 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText() !== 'DELETE') {
      setDeleteError('Please type DELETE to confirm');
      return;
    }

    setDeleteLoading(true);
    setDeleteError(null);

    try {
      const token = await getAccessToken();
      if (!token) throw new Error('Not authenticated');

      const response = await fetch(`${API_BASE}/api/me`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete account');
      }

      await signOut();
      navigate('/login');
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete account');
      setDeleteLoading(false);
    }
  };

  return (
    <div class="max-w-2xl">
      <h1 class="text-2xl font-bold mb-6">Settings</h1>

      {/* Profile Section */}
      <section class="bg-gray-800/50 border border-gray-700 rounded-xl p-6 mb-6">
        <h2 class="text-lg font-medium mb-4">Profile</h2>
        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-400 mb-1">Email</label>
            <p class="text-white">{currentUser?.email}</p>
          </div>
          <Show when={currentUser?.user_metadata?.full_name}>
            <div>
              <label class="block text-sm font-medium text-gray-400 mb-1">Name</label>
              <p class="text-white">{currentUser?.user_metadata?.full_name}</p>
            </div>
          </Show>
          <div>
            <label class="block text-sm font-medium text-gray-400 mb-1">User ID</label>
            <p class="text-gray-500 font-mono text-sm">{currentUser?.id}</p>
          </div>
        </div>
      </section>

      {/* Administration Section */}
      <section class="bg-gray-800/50 border border-gray-700 rounded-xl p-6 mb-6">
        <h2 class="text-lg font-medium mb-4">Administration</h2>
        <div class="space-y-3">
          <A
            href="/settings/models"
            class="flex items-center justify-between p-4 bg-gray-900/50 border border-gray-600 rounded-lg hover:border-purple-500/50 transition-colors group"
          >
            <div class="flex items-center gap-3">
              <div class="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <h3 class="font-medium text-white group-hover:text-purple-400 transition-colors">LLM Models</h3>
                <p class="text-sm text-gray-400">Configure available LLM providers and models</p>
              </div>
            </div>
            <svg class="w-5 h-5 text-gray-500 group-hover:text-purple-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
            </svg>
          </A>
        </div>
      </section>

      {/* Password Change Section */}
      <section class="bg-gray-800/50 border border-gray-700 rounded-xl p-6 mb-6">
        <h2 class="text-lg font-medium mb-4">Change Password</h2>

        <Show when={passwordSuccess()}>
          <div class="mb-4 p-3 bg-green-500/20 border border-green-500/50 rounded-lg">
            <p class="text-green-400 text-sm">Password changed successfully!</p>
          </div>
        </Show>

        <Show when={passwordError()}>
          <div class="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg">
            <p class="text-red-400 text-sm">{passwordError()}</p>
          </div>
        </Show>

        <form onSubmit={handlePasswordChange} class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-300 mb-1.5">Current Password</label>
            <input
              type="password"
              value={currentPassword()}
              onInput={(e) => setCurrentPassword(e.currentTarget.value)}
              class="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-300 mb-1.5">New Password</label>
            <input
              type="password"
              value={newPassword()}
              onInput={(e) => setNewPassword(e.currentTarget.value)}
              minLength={8}
              class="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-300 mb-1.5">
              Confirm New Password
            </label>
            <input
              type="password"
              value={confirmPassword()}
              onInput={(e) => setConfirmPassword(e.currentTarget.value)}
              minLength={8}
              class="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
          <button
            type="submit"
            disabled={passwordLoading() || !currentPassword() || !newPassword()}
            class="px-6 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-medium rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-purple-500/20"
          >
            {passwordLoading() ? 'Changing...' : 'Change Password'}
          </button>
        </form>
      </section>

      {/* Danger Zone */}
      <section class="bg-red-500/10 border border-red-500/30 rounded-xl p-6">
        <h2 class="text-lg font-medium text-red-400 mb-2">Danger Zone</h2>
        <p class="text-gray-400 text-sm mb-4">
          Once you delete your account, there is no going back. All your expressions, API keys, and
          data will be permanently deleted.
        </p>

        <Show
          when={!showDeleteConfirm()}
          fallback={
            <div class="space-y-4">
              <Show when={deleteError()}>
                <div class="p-3 bg-red-500/20 border border-red-500/50 rounded-lg">
                  <p class="text-red-400 text-sm">{deleteError()}</p>
                </div>
              </Show>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1.5">
                  Type <span class="font-mono text-red-400">DELETE</span> to confirm
                </label>
                <input
                  type="text"
                  value={deleteConfirmText()}
                  onInput={(e) => setDeleteConfirmText(e.currentTarget.value)}
                  placeholder="DELETE"
                  class="w-full px-4 py-3 bg-gray-700/50 border border-red-500/50 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>
              <div class="flex gap-3">
                <button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setDeleteConfirmText('');
                    setDeleteError(null);
                  }}
                  class="px-4 py-2.5 bg-gray-700 hover:bg-gray-600 text-white font-medium rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteAccount}
                  disabled={deleteLoading() || deleteConfirmText() !== 'DELETE'}
                  class="px-4 py-2.5 bg-red-600 hover:bg-red-500 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deleteLoading() ? 'Deleting...' : 'Delete My Account'}
                </button>
              </div>
            </div>
          }
        >
          <button
            onClick={() => setShowDeleteConfirm(true)}
            class="px-4 py-2.5 bg-red-600/20 hover:bg-red-600/30 text-red-400 font-medium rounded-lg border border-red-500/50 transition-colors"
          >
            Delete Account
          </button>
        </Show>
      </section>
    </div>
  );
};

export default Settings;
